<template>
  <v-card class="staff-order-shop pa-4 w-100">
    <StaffOrderHeader
      :order="order"
      :busy="busy"
      :editable-buyer="isOpen"
      receipt-toggle-mode
      :receipt-disabled="!receiptEditable"
      :print-selected="receiptSelection.print"
      :email-selected="receiptSelection.email"
      :email-disabled="!hasBuyerEmail"
      closable
      @edit-buyer="buyerDialog = true"
      @print="toggleReceipt('print')"
      @email="toggleReceipt('email')"
      @close="emit('close')"
    >
      <template #details>
        <div
          v-if="receiptEditable"
          class="receipt-delivery text-caption d-flex align-center flex-wrap ga-2"
        >
          <span v-if="!hasBuyerEmail">Email requires a valid buyer email.</span>
          <span v-if="receiptSelection.print && !selectedReceiptPrinter">
            Choose a receipt printer, or turn Print receipt off to continue.
          </span>
          <v-btn
            v-if="
              !receiptReady &&
              (!receiptSelection.print || selectedReceiptPrinter)
            "
            size="small"
            :disabled="busy"
            @click="act(ensureReceiptDelivery)"
            >Retry saving receipt choices</v-btn
          >
        </div>
      </template>
    </StaffOrderHeader>
    <v-progress-linear v-if="busy || loadingCatalog" indeterminate />
    <v-alert v-if="errorMessage" type="error" class="my-3">{{
      errorMessage
    }}</v-alert>
    <v-alert v-if="pollError" type="warning" class="my-3">
      Payment status could not be refreshed: {{ pollError }}. Automatic checks
      will retry.
    </v-alert>
    <v-alert v-if="pendingReceiptChannels.length" type="warning" class="my-3">
      Receipt delivery is pending: {{ pendingReceiptChannels.join("; ") }}.
      <v-btn
        :disabled="busy"
        variant="text"
        @click="act(session.retryReceiptDelivery)"
        >Retry receipt delivery</v-btn
      >
    </v-alert>
    <template v-if="!order">
      <p v-if="busy">Opening order...</p>
      <v-btn v-else :disabled="busy" @click="openOrder"
        >Retry opening order</v-btn
      >
    </template>
    <template v-else>
      <v-row class="shop-body">
        <v-col cols="12" md="8" xl="9" class="shop-tools">
          <v-card class="shop-edit-card pa-4" color="shop-panel" variant="flat">
            <template v-if="isOpen">
              <StaffCatalogPurchase
                ref="catalogPurchase"
                :key="`${order.assignId}-${initialPurchaseComplete}`"
                :items="instances"
                :item-catalog-names="itemCatalogNames"
                :item-category-ids="itemCategoryIds"
                :shop-categories="shopCategories"
                :buyer="
                  order.personId ? { personId: order.personId.personId } : null
                "
                :buyer-name="order.personId?.name ?? null"
                :default-recipient="
                  contextBuyerId == null ? null : { personId: contextBuyerId }
                "
                :default-recipient-name="pagePersonName"
                :disabled="busy"
                :loading="loadingCatalog"
                :item-type="
                  initialPurchaseComplete ? undefined : props.itemType
                "
                :membership-id="
                  initialPurchaseComplete ? undefined : props.membershipId
                "
                :restock="
                  !initialPurchaseComplete && props.restockBalanceType != null
                "
                :initial-balance-number="
                  initialPurchaseComplete ? undefined : props.balanceNumber
                "
                :on-add="add"
              />
            </template>
            <div class="payment-panel" v-else-if="showPaymentInput">
              <h3>Payment</h3>
              <v-alert v-if="isComplete" type="warning" class="mb-3">
                A replacement payment is required. Purchased items remain
                active; this does not reopen or change the cart.
              </v-alert>
              <v-tabs
                :model-value="paymentType"
                @update:model-value="changePaymentType"
                class="payment-type-tabs mb-4"
                color="primary"
                height="80"
                show-arrows
                aria-label="Payment type"
              >
                <v-tab
                  v-for="type in paymentTypes"
                  :key="type.value"
                  :value="type.value"
                  :disabled="
                    busy ||
                    (type.value === 'TERMINAL' &&
                      order.orderSource === 'ONLINE')
                  "
                  class="payment-type-tab"
                >
                  <div class="d-flex flex-column align-center ga-2">
                    <v-icon :icon="type.icon" size="28" />
                    {{ type.title }}
                  </div>
                </v-tab>
              </v-tabs>
              <div class="payment-inputs">
                <div class="d-flex justify-space-between mb-3">
                  <span>Remaining to pay</span>
                  <strong>{{ money(availableCents) }}</strong>
                </div>
                <CurrencyInput
                  :key="paymentType"
                  v-model:amount-cents="paymentCents"
                  :label="
                    paymentType === PaymentType.CASH
                      ? 'Cash collected'
                      : 'Payment amount'
                  "
                  :disabled="busy"
                  required
                />
                <p class="text-caption mt-2 mb-3">
                  Enter a smaller amount to split this order across payment
                  methods.
                </p>
                <template v-if="paymentType === PaymentType.CASH">
                  <v-alert
                    v-if="validAmount(paymentCents)"
                    type="info"
                    variant="tonal"
                    class="mb-3"
                  >
                    Applying {{ money(paymentAmountCents ?? null) }} to the
                    order.
                    <strong>Change due: {{ money(cashChangeCents) }}</strong>
                  </v-alert>
                  <v-select
                    v-model="drawerId"
                    :items="drawers"
                    item-title="cashDrawerName"
                    item-value="cashDrawerId"
                    label="Cash drawer"
                  />
                </template>
                <StaffBalancePaymentFields
                  v-if="paymentType === PaymentType.BALANCE"
                  v-model="paymentBalanceNumber"
                  :buyer-id="order.personId?.personId ?? null"
                  :disabled="busy"
                />
                <template
                  v-if="
                    [
                      PaymentType.CHECK,
                      PaymentType.BANK_TRANSFER,
                      PaymentType.OTHER,
                    ].some((type) => type === paymentType)
                  "
                >
                  <v-text-field
                    v-model="reference"
                    label="Reference / check number"
                  />
                  <v-text-field v-model="memo" label="Payment note" />
                  <v-text-field
                    v-if="paymentType === PaymentType.OTHER"
                    v-model="otherType"
                    label="Payment type description"
                  />
                </template>
                <v-select
                  v-if="paymentType === 'TERMINAL'"
                  v-model="readerId"
                  :items="
                    readers.filter((reader) => reader.status === 'online')
                  "
                  item-title="label"
                  item-value="readerId"
                  label="Stripe reader"
                  :disabled="busy"
                />
                <StaffStripeReaderSimulator
                  v-if="paymentType === 'TERMINAL'"
                  :reader-id="readerId"
                  :disabled="busy"
                  :outcome="simulationOutcome"
                  @simulate="simulateReader"
                />
                <StaffOrderCardPayment
                  v-if="
                    paymentType === 'MANUAL_CARD' &&
                    activeCardAttempt?.clientSecret
                  "
                  ref="cardForm"
                  :key="activeCardAttempt.clientSecret"
                  :client-secret="activeCardAttempt.clientSecret"
                  :customer-session="
                    () => customerSession(activeCardAttempt!.payment)
                  "
                  :disabled="
                    working ||
                    sessionBusy ||
                    !receiptReady ||
                    activeCardAttempt.amountCents !== paymentCents
                  "
                  hide-actions
                  @processing="cardProcessing = $event"
                  @confirmed="reconcile"
                />
                <p
                  v-else-if="paymentType === 'MANUAL_CARD'"
                  class="text-caption"
                >
                  The card form loads automatically for the entered amount.
                </p>
                <div
                  class="payment-input-actions d-flex flex-wrap justify-end ga-2 mt-3"
                  role="group"
                  aria-label="Payment actions"
                >
                  <v-btn
                    v-if="paymentType === 'TERMINAL'"
                    :disabled="busy"
                    @click="act(loadReaders)"
                    >Refresh readers</v-btn
                  >
                  <v-btn
                    v-if="
                      (paymentType === 'TERMINAL' ||
                        paymentType === 'MANUAL_CARD') &&
                      activeCardAttempt &&
                      !activeCardAttempt.clientSecret &&
                      (paymentType === 'TERMINAL' ||
                        activeCardAttempt.amountCents === paymentCents)
                    "
                    :disabled="busy || !receiptReady"
                    @click="
                      act(() =>
                        session.preparePayment(activeCardAttempt!.payment),
                      )
                    "
                    >Retry card setup</v-btn
                  >
                  <v-btn
                    v-if="
                      paymentType === 'MANUAL_CARD' &&
                      (!activeCardAttempt ||
                        activeCardAttempt.amountCents !== paymentCents) &&
                      errorMessage
                    "
                    :disabled="busy || !canStartPayment"
                    @click="prepareManualCard"
                    >Retry card setup</v-btn
                  >
                  <v-btn
                    v-if="paymentType === 'MANUAL_CARD'"
                    color="primary"
                    variant="flat"
                    :disabled="
                      busy ||
                      !cardForm?.canConfirm ||
                      activeCardAttempt?.amountCents !== paymentCents
                    "
                    @click="cardForm?.confirm()"
                    >Confirm card payment</v-btn
                  >
                  <v-btn
                    v-else
                    color="primary"
                    variant="flat"
                    :disabled="busy || !canStartPayment"
                    @click="startPayment"
                    >{{
                      paymentType === "TERMINAL"
                        ? "Send to terminal"
                        : "Authorize payment"
                    }}</v-btn
                  >
                </div>
                <p class="mt-2">
                  Items activate when the entire order is covered. Card payments
                  capture at 23:50 New York time, or immediately after that
                  cutoff.
                </p>
              </div>
            </div>
            <v-alert
              v-if="cashChangeDueCents > 0"
              type="info"
              variant="tonal"
              icon="mdi-cash-refund"
              class="shop-change-due mt-3"
              role="status"
            >
              <strong>Change due: {{ money(cashChangeDueCents) }}</strong>
              <div class="text-caption">From the last cash payment.</div>
            </v-alert>
            <v-alert
              v-if="isComplete"
              type="success"
              class="shop-completion-notice"
            >
              {{
                order.orderStatus === OrderStatus.PROCESSED
                  ? "Purchased items active"
                  : "Order complete"
              }}
              <template v-if="hasDeferredPayment">
                - card payment authorized; capture is scheduled for 23:50 New
                York time.
              </template>
            </v-alert>
          </v-card>
        </v-col>
        <v-col cols="12" md="4" xl="3" class="cart-panel">
          <v-card
            class="shop-summary-card pa-2"
            color="shop-panel"
            variant="flat"
          >
            <div v-if="isOpen" class="cart-discounts mb-2">
              <div class="text-subtitle-2 mb-1">Discounts</div>
              <v-text-field
                v-model="code"
                label="Discount code"
                density="compact"
                hide-details
                class="mb-2"
                @keyup.enter.prevent="applyCode"
              >
                <template #append-inner>
                  <v-btn
                    size="small"
                    variant="text"
                    :disabled="busy || !code.trim()"
                    @click="applyCode"
                    >Apply</v-btn
                  >
                </template>
              </v-text-field>
              <v-select
                :model-value="null"
                :items="addableDiscounts"
                :item-title="discountTitle"
                item-value="discountId"
                :label="
                  addableDiscounts.length
                    ? 'Add discount'
                    : 'No more eligible discounts'
                "
                density="compact"
                hide-details
                :disabled="busy || !addableDiscounts.length"
                @update:model-value="addDiscount"
              />
            </div>
            <OrderReceipt
              summary
              :order="order"
              :lines="lines"
              :line-details="receiptLineDetails"
              :item-title="
                (line) => lineNames[line.assignId] ?? `Line #${line.assignId}`
              "
              :authorized-cents="authorizedCents"
            >
              <template #line-configure="{ line }">
                <v-btn
                  v-if="canConfigureLine(line)"
                  :disabled="busy"
                  icon="mdi-cog"
                  size="x-small"
                  density="comfortable"
                  variant="text"
                  :aria-label="`Configure ${lineNames[line.assignId] ?? `line ${line.assignId}`}`"
                  @click="configureLine(line)"
                />
              </template>
              <template #recipient="{ line, depth, label }">
                <v-chip
                  v-if="!depth && canChangeRecipient(line)"
                  density="compact"
                  class="receipt-recipient"
                  role="button"
                  :disabled="busy"
                  :aria-label="`Change who ${lineNames[line.assignId] ?? `line ${line.assignId}`} is for`"
                  @click="recipientLineId = line.assignId"
                  >{{ label ?? "No recipient" }}</v-chip
                >
                <v-chip
                  v-else-if="!depth && label"
                  density="compact"
                  class="receipt-recipient"
                  >{{ label }}</v-chip
                >
              </template>
              <template #line-actions="{ line }">
                <v-btn
                  v-if="isOpen"
                  :disabled="busy"
                  icon="mdi-delete"
                  variant="text"
                  :aria-label="`Remove ${lineNames[line.assignId] ?? `line ${line.assignId}`}`"
                  @click="act(() => session.remove(line))"
                />
              </template>
              <template #discount="{ line, discount }">
                <StaffLineDiscountChip
                  :discount="discount"
                  :catalog-type="
                    isOpen ? lineDiscountType(line.assignId, discount) : null
                  "
                  :line-label="
                    lineNames[line.assignId] ?? `line ${line.assignId}`
                  "
                  :removable="isOpen && !!discount.discount"
                  :disabled="busy"
                  @remove="
                    act(() =>
                      session.unapplyLineDiscount(line, discount.discount!),
                    )
                  "
                  @update="(terms) => changeLineDiscount(line, discount, terms)"
                />
              </template>
              <template v-if="checkoutPayments.length" #payments>
                <OrderPaymentDetails
                  class="mt-3"
                  :payments="checkoutPayments"
                  :attempts="attempts"
                  compact
                >
                  <template #actions="{ payment }">
                    <v-btn
                      v-if="canCancelReceiptPayment(payment)"
                      icon="mdi-delete"
                      variant="text"
                      :disabled="busy"
                      :aria-label="`Cancel payment #${receiptPaymentId(payment)}`"
                      @click="cancelReceiptPayment(payment)"
                    />
                  </template>
                </OrderPaymentDetails>
              </template>
            </OrderReceipt>
          </v-card>
        </v-col>
      </v-row>
      <v-card-actions class="shop-actions flex-wrap ga-2">
        <div
          class="shop-cancel-actions d-flex flex-wrap ga-2"
          role="group"
          aria-label="Cancel and close options"
        >
          <v-btn
            v-if="order.orderStatus === OrderStatus.PAYING"
            :disabled="busy"
            @click="act(session.reopen)"
            >Back to cart</v-btn
          >
          <v-btn
            v-if="
              isOpen ||
              order.orderStatus === OrderStatus.PAYING ||
              canVoidDeferredOrder
            "
            color="error"
            :disabled="busy"
            @click="cancelDialog = true"
            >{{
              canVoidDeferredOrder ? "Void uncaptured order" : "Cancel order"
            }}</v-btn
          >
          <v-btn :disabled="busy" @click="emit('close')">{{
            isComplete ? "Done" : "Close (keep order)"
          }}</v-btn>
        </div>
        <v-spacer />
        <div
          class="shop-proceed-actions d-flex flex-wrap ga-2"
          role="group"
          aria-label="Payment and proceed options"
        >
          <v-btn v-if="isComplete" :disabled="busy" @click="newSale"
            >New sale</v-btn
          >
          <v-btn :disabled="busy" @click="act(session.refresh)">Refresh</v-btn>
          <v-btn
            v-if="isOpen"
            color="primary"
            variant="flat"
            :disabled="
              busy || checkingAgreements || !receiptReady || !lines.length
            "
            @click="checkout"
            >To payments</v-btn
          >
          <v-btn
            v-if="
              order.orderStatus === OrderStatus.PAYING ||
              hasDeferredPayment ||
              hasPendingPayment
            "
            :disabled="busy || !receiptReady"
            @click="reconcile"
            >Check payments</v-btn
          >
        </div>
      </v-card-actions>
    </template>
    <v-dialog v-model="printerDialog" max-width="600">
      <v-card>
        <StaffProfile />
        <v-card-actions>
          <v-btn @click="printerDialog = false">Close printer selection</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="buyerDialog" max-width="900">
      <v-card title="Choose buyer" class="pa-4">
        <p class="mb-2">
          The buyer owns and pays for the order. Items keep the people they are
          for.
        </p>
        <GeneratedPersonSearch picker @person-selected="changeBuyer" />
        <v-card-actions
          ><v-btn :disabled="busy" @click="buyerDialog = false"
            >Close</v-btn
          ></v-card-actions
        >
      </v-card>
    </v-dialog>
    <v-dialog v-model="recipientDialog" max-width="900">
      <v-card title="Choose who this item is for" class="pa-4">
        <GeneratedPersonSearch picker @person-selected="changeLineRecipient" />
        <v-card-actions
          ><v-btn :disabled="busy" @click="recipientLineId = null"
            >Close</v-btn
          ></v-card-actions
        >
      </v-card>
    </v-dialog>
    <v-dialog v-model="cancelDialog" max-width="400">
      <v-card
        title="Cancel this order?"
        :text="
          canVoidDeferredOrder
            ? 'The card authorization will be released and purchased items voided. Only same-day orders with no settled payments can be voided. To change the payment method, start a new sale after voiding.'
            : 'Pending payments will be cancelled and unpurchased items released.'
        "
      >
        <v-card-actions>
          <v-btn :disabled="busy" @click="cancelDialog = false"
            >Keep order</v-btn
          >
          <v-btn color="error" :disabled="busy" @click="cancelOrder"
            >Cancel order</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      :model-value="paymentToCancel != null"
      :persistent="busy"
      max-width="450"
      @update:model-value="!$event && !busy && (paymentToCancel = null)"
    >
      <v-card
        title="Cancel this payment?"
        text="The uncaptured authorization will be released. Purchased items stay active. Take a replacement payment on this same order; captured payments require a refund."
      >
        <v-alert v-if="errorMessage" type="error" class="mx-4">{{
          errorMessage
        }}</v-alert>
        <v-card-actions>
          <v-btn :disabled="busy" @click="paymentToCancel = null"
            >Keep payment</v-btn
          >
          <v-btn color="error" :disabled="busy" @click="cancelCompletedPayment"
            >Cancel payment</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script setup lang="ts">
import {
  computed,
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  watch,
  type Ref,
} from "vue";
import type { PersonStaff as Person } from "../../../cbi-generator/generated/dto/PersonStaff";
import type { PersonStaffSearchResult } from "../../../cbi-generator/generated/dto/PersonStaffSearchResult";
import { usePersonService } from "../../../cbi-generator/frontend/composables/usePersonFrontendService";
import type { CatalogItemRef } from "../../../cbi-generator/generated/dto/CatalogItemRef";
import { errorMessage as requestErrorMessage } from "@/composables/generated/contracts";
import { useOrderService } from "../../../cbi-generator/frontend/composables/useOrderFrontendService";
import { usePaymentService } from "../../../cbi-generator/frontend/composables/usePaymentFrontendService";
import { useCatalogService } from "../../../cbi-generator/frontend/composables/useCatalogFrontendService";
import { useDiscountService } from "../../../cbi-generator/frontend/composables/useDiscountFrontendService";
import { useCashDrawerService } from "../../../cbi-generator/frontend/composables/useCashDrawerFrontendService";
import { createStaffOrderSession } from "@/composables/shop/staffOrderSession";
import { emptySearchParams } from "@/composables/useTableHelper";
import { useNotificationStore } from "@/stores/notification";
import { useStripeService } from "../../../cbi-generator/frontend/composables/useStripeFrontendService";
import type { StripeReader } from "../../../cbi-generator/generated/dto/StripeReader";
import { loadStaffCatalog } from "@/composables/shop/staffCatalog";
import type { BalanceType } from "../../../cbi-generator/generated/dto/BalanceType";
import type { CatalogPurchaseItem } from "@/composables/shop/catalogPurchase";
import type { CashDrawerStaffSearchParams } from "../../../cbi-generator/generated/dto/CashDrawerStaffSearchParams";
import { CashDrawerStaffSearchParamsMeta } from "../../../cbi-generator/generated/dto/CashDrawerStaffSearchParamsMeta";
import type { CashDrawerStaffSearchResult } from "../../../cbi-generator/generated/dto/CashDrawerStaffSearchResult";
import { useReceiptPrinter } from "@/composables/shop/useReceiptPrinter";
import type { PaginationResult } from "../../../cbi-generator/generated/dto/PaginationResult";
import type { DiscountCandidate } from "../../../cbi-generator/generated/dto/DiscountCandidate";
import { DiscountType } from "../../../cbi-generator/generated/dto/DiscountType";
import type { CatalogDiscountStaff } from "../../../cbi-generator/generated/dto/CatalogDiscountStaff";
import type { AddToCart } from "../../../cbi-generator/generated/dto/AddToCart";
import { OrderStatus } from "../../../cbi-generator/generated/dto/OrderStatus";
import type { PaymentAttempt } from "../../../cbi-generator/generated/dto/PaymentAttempt";
import { PaymentType } from "../../../cbi-generator/generated/dto/PaymentType";
import { PaymentStatus } from "../../../cbi-generator/generated/dto/PaymentStatus";
import type { OrderPaymentStaff } from "../../../cbi-generator/generated/dto/OrderPaymentStaff";
import type { OrderPaymentRef } from "../../../cbi-generator/generated/dto/OrderPaymentRef";
import { SortOrder } from "../../../cbi-generator/generated/dto/SortOrder";
import StaffOrderCardPayment from "./StaffOrderCardPayment.vue";
import StaffCatalogPurchase from "./StaffCatalogPurchase.vue";
import OrderReceipt, { type ReceiptLine } from "./OrderReceipt.vue";
import StaffBalancePaymentFields from "./StaffBalancePaymentFields.vue";
import StaffStripeReaderSimulator from "./StaffStripeReaderSimulator.vue";
import StaffLineDiscountChip, {
  type DiscountTerms,
} from "./StaffLineDiscountChip.vue";
import type { LineItemRef } from "../../../cbi-generator/generated/dto/LineItemRef";
import type { StripeReaderSimulation } from "../../../cbi-generator/generated/dto/StripeReaderSimulation";
import { useBalanceService } from "../../../cbi-generator/frontend/composables/useBalanceFrontendService";
import {
  loadReceiptLineDetails,
  type ReceiptLineDetails,
} from "@/composables/shop/receiptLineDetails";
import StaffOrderHeader from "./StaffOrderHeader.vue";
import OrderPaymentDetails from "./OrderPaymentDetails.vue";
import { ItemType } from "../../../cbi-generator/generated/dto/ItemType";
import type { CategoryStaff } from "../../../cbi-generator/generated/dto/CategoryStaff";
import StaffProfile from "@/pages/staff/StaffProfile.vue";

const props = defineProps<{
  personId?: number;
  orderId?: number;
  itemType?: ItemType;
  membershipId?: number;
  balanceNumber?: string;
  restockBalanceType?: BalanceType;
}>();
const initialPurchaseComplete = ref(false);
const emit = defineEmits<{
  close: [];
  complete: [];
  "order-selected": [id: number | null];
}>();
const pagePerson = inject<Ref<Person | null> | undefined>("person", undefined);
const contextBuyerId = computed(
  () => props.personId ?? pagePerson?.value?.personId ?? null,
);
const pagePersonName = computed(() =>
  pagePerson?.value && pagePerson.value.personId === contextBuyerId.value
    ? pagePerson.value.name
    : null,
);
const buyerDialog = ref(false);
let existingOrderId = props.orderId;
const paymentService = usePaymentService();
const session = createStaffOrderSession({
  orders: useOrderService(),
  payments: paymentService,
});
const customerSession = async (payment: OrderPaymentRef) =>
  (await paymentService.paymentCustomerSessionStaff(payment)).clientSecret;
const {
  order,
  orderRef,
  lines,
  payments,
  discounts,
  attempts,
  busy: sessionBusy,
  isOpen,
  isComplete,
  availableCents,
} = session;
const hasPendingPayment = computed(() =>
  attempts.value.some((attempt) => attempt.status === PaymentStatus.PENDING),
);
const checkoutPayments = computed(() => {
  const recorded = new Set(payments.value.map((payment) => payment.assignId));
  return [
    ...payments.value,
    ...attempts.value.filter(
      (attempt) => !recorded.has(attempt.payment.assignId),
    ),
  ];
});
const authorizedCents = computed(() =>
  payments.value.reduce(
    (sum, payment) =>
      sum +
      (payment.paymentStatus === PaymentStatus.CAPTURABLE
        ? (payment.paymentCapturableCents ?? 0)
        : 0),
    0,
  ),
);
const paymentToCancel = shallowRef<OrderPaymentStaff | null>(null);
const hasDeferredPayment = computed(
  () =>
    isComplete.value &&
    payments.value.some(
      (payment) =>
        payment.paymentType === PaymentType.STRIPE &&
        payment.paymentStatus === PaymentStatus.CAPTURABLE,
    ),
);
const canVoidDeferredOrder = computed(
  () =>
    order.value?.orderStatus === OrderStatus.PROCESSED &&
    hasDeferredPayment.value &&
    !payments.value.some((payment) =>
      [
        PaymentStatus.SUCCESSFUL,
        PaymentStatus.REFUNDED,
        PaymentStatus.PARTIALLY_REFUNDED,
      ].includes(payment.paymentStatus),
    ),
);
const working = ref(false);
const cardProcessing = ref(false);
const busy = computed(
  () => working.value || sessionBusy.value || cardProcessing.value,
);
const checkingAgreements = ref(false);
const catalog = useCatalogService();
const notification = useNotificationStore();
const errorMessage = ref("");
const pollError = ref("");
const loadingCatalog = ref(false);
const instances = shallowRef<CatalogPurchaseItem[]>([]);
const itemCatalogNames = shallowRef<ReadonlyMap<number, string>>(new Map());
const itemCategoryIds = shallowRef<ReadonlyMap<number, readonly number[]>>(
  new Map(),
);
const itemAgreementIds = shallowRef<ReadonlyMap<number, readonly number[]>>(
  new Map(),
);
const categoryAgreementIds = shallowRef<ReadonlyMap<number, readonly number[]>>(
  new Map(),
);
const globalAgreementIds = shallowRef<readonly number[]>([]);
const agreementNames = shallowRef<ReadonlyMap<number, string>>(new Map());
const shopCategories = shallowRef<CategoryStaff[]>([]);
const drawers = shallowRef<CashDrawerStaffSearchResult[]>([]);
const readers = shallowRef<StripeReader[]>([]);
const lineNames = shallowRef<Record<number, string>>({});
const receiptLineDetails = shallowRef<Record<number, ReceiptLineDetails>>({});
const discountDetails = shallowRef<CatalogDiscountStaff[]>([]);
const code = ref("");
const discountGroups = computed(() => [
  ...new Map(
    discounts.value.map((discount) => [discount.discountId, discount]),
  ).values(),
]);
const appliedDiscountIds = (lineId: number) =>
  new Set(
    (receiptLineDetails.value[lineId]?.discounts ?? []).map(
      (discount) => discount.discountId,
    ),
  );
/** Eligible lines that don't have [discountId] yet. */
const linesMissingDiscount = (discountId: number) =>
  discounts.value.filter(
    (candidate) =>
      candidate.discountId === discountId &&
      !appliedDiscountIds(candidate.lineItem.assignId).has(discountId),
  );
const addableDiscounts = computed(() =>
  discountGroups.value.filter(
    (discount) => linesMissingDiscount(discount.discountId).length > 0,
  ),
);
const lineDiscountType = (
  lineId: number,
  discount: ReceiptLineDetails["discounts"][number],
) =>
  discounts.value.find(
    (candidate) =>
      candidate.lineItem.assignId === lineId &&
      candidate.discountId === discount.discountId,
  )?.discountType ?? null;
type PaymentChoice =
  | Exclude<PaymentType, PaymentType.STRIPE>
  | "TERMINAL"
  | "MANUAL_CARD";
const paymentType = ref<PaymentChoice>("TERMINAL");
const cardForm = ref<InstanceType<typeof StaffOrderCardPayment>>();
const activeCardAttempt = computed(() =>
  attempts.value.find(
    (attempt) =>
      attempt.status === PaymentStatus.PENDING &&
      (attempt.clientSecret ||
        payments.value.some(
          (payment) =>
            payment.assignId === attempt.payment.assignId &&
            payment.paymentType === PaymentType.STRIPE,
        )),
  ),
);
const cancellableAttempts = computed(() =>
  order.value?.orderStatus === OrderStatus.PAYING
    ? attempts.value.filter((attempt) =>
        [PaymentStatus.PENDING, PaymentStatus.CAPTURABLE].includes(
          attempt.status,
        ),
      )
    : [],
);
const cancellablePayments = computed(() =>
  order.value?.orderStatus === OrderStatus.PROCESSED
    ? payments.value.filter((payment) =>
        [PaymentStatus.PENDING, PaymentStatus.CAPTURABLE].includes(
          payment.paymentStatus,
        ),
      )
    : [],
);
const receiptPaymentId = (payment: OrderPaymentStaff | PaymentAttempt) =>
  "assignId" in payment ? payment.assignId : payment.payment.assignId;
function canCancelReceiptPayment(payment: OrderPaymentStaff | PaymentAttempt) {
  const id = receiptPaymentId(payment);
  return (
    cancellableAttempts.value.some(
      (attempt) => attempt.payment.assignId === id,
    ) || cancellablePayments.value.some((stored) => stored.assignId === id)
  );
}
function cancelReceiptPayment(payment: OrderPaymentStaff | PaymentAttempt) {
  const id = receiptPaymentId(payment);
  const attempt = cancellableAttempts.value.find(
    (attempt) => attempt.payment.assignId === id,
  );
  if (attempt) void cancelAttempt(attempt);
  else {
    const stored = cancellablePayments.value.find(
      (stored) => stored.assignId === id,
    );
    if (stored) paymentToCancel.value = stored;
  }
}
const showPaymentInput = computed(
  () =>
    order.value?.orderStatus === OrderStatus.PAYING ||
    (order.value?.orderStatus === OrderStatus.PROCESSED &&
      ((availableCents.value ?? 0) > 0 || hasPendingPayment.value)),
);
const paymentTypes: { value: PaymentChoice; title: string; icon: string }[] = [
  { value: "TERMINAL", title: "Terminal", icon: "mdi-contactless-payment" },
  { value: "MANUAL_CARD", title: "Manual Card", icon: "mdi-credit-card" },
  { value: PaymentType.CASH, title: "Cash", icon: "mdi-cash" },
  { value: PaymentType.BALANCE, title: "Balance", icon: "mdi-wallet-giftcard" },
  { value: PaymentType.CHECK, title: "Check", icon: "mdi-checkbook" },
  {
    value: PaymentType.BANK_TRANSFER,
    title: "Bank transfer",
    icon: "mdi-bank-transfer",
  },
  { value: PaymentType.OTHER, title: "Other", icon: "mdi-dots-horizontal" },
];
const paymentCents = ref<number>();
const paymentAmountCents = computed(() =>
  paymentType.value === PaymentType.CASH && paymentCents.value != null
    ? Math.min(paymentCents.value, Math.max(0, availableCents.value ?? 0))
    : paymentCents.value,
);
const cashChangeCents = computed(() =>
  Math.max(0, (paymentCents.value ?? 0) - (availableCents.value ?? 0)),
);
// Change owed from the last cash tender; kept after payment so staff can hand it back.
const lastCashChange = shallowRef<{ orderId: number; cents: number } | null>(
  null,
);
const cashChangeDueCents = computed(() =>
  lastCashChange.value && lastCashChange.value.orderId === order.value?.assignId
    ? lastCashChange.value.cents
    : 0,
);
const canStartPayment = computed(
  () =>
    receiptReady.value &&
    validAmount(paymentAmountCents.value) &&
    paymentAmountCents.value! <=
      (availableCents.value ?? 0) +
        (paymentType.value === "MANUAL_CARD" || paymentType.value === "TERMINAL"
          ? (activeCardAttempt.value?.amountCents ?? 0)
          : 0) &&
    (paymentType.value !== PaymentType.CASH || !!drawerId.value) &&
    (paymentType.value !== PaymentType.BALANCE ||
      !!paymentBalanceNumber.value.trim()) &&
    (paymentType.value !== "TERMINAL" || !!readerId.value.trim()),
);
let manualSetupTimer: ReturnType<typeof setTimeout> | undefined;
let suppressManualSetup = false;
let manualSetupRequested = false;
async function changePaymentType(value: unknown) {
  const choice = paymentTypes.find((type) => type.value === value)?.value;
  if (!choice || busy.value || choice === paymentType.value) return;
  if (choice === "TERMINAL" && order.value?.orderSource === "ONLINE") return;
  const previous = paymentType.value;
  const backendType =
    previous === "TERMINAL" || previous === "MANUAL_CARD"
      ? PaymentType.STRIPE
      : previous;
  const pending = attempts.value.filter(
    (attempt) =>
      attempt.status === PaymentStatus.PENDING &&
      (backendType === PaymentType.STRIPE
        ? attempt === activeCardAttempt.value
        : payments.value.some(
            (payment) =>
              payment.assignId === attempt.payment.assignId &&
              payment.paymentType === backendType,
          )),
  );
  clearTimeout(manualSetupTimer);
  suppressManualSetup = true;
  manualSetupRequested = false;
  if (
    !(previous === "TERMINAL" && choice === "MANUAL_CARD") &&
    pending.length &&
    !(await act(async () => {
      for (const attempt of pending)
        await session.cancelPayment(attempt.payment);
    }))
  )
    return;
  paymentType.value = choice;
}
async function cancelAttempt(attempt: PaymentAttempt) {
  suppressManualSetup = true;
  manualSetupRequested = false;
  await act(() => session.cancelPayment(attempt.payment));
}
async function prepareManualCard() {
  if (
    disposed ||
    busy.value ||
    !showPaymentInput.value ||
    !receiptReady.value ||
    paymentType.value !== "MANUAL_CARD" ||
    !canStartPayment.value
  )
    return;
  manualSetupRequested = false;
  const amount = paymentCents.value!;
  const existing = activeCardAttempt.value;
  if (existing?.amountCents === amount) return;
  if (existing) {
    if (!(await act(() => session.cancelPayment(existing.payment)))) return;
  }
  if (
    disposed ||
    paymentType.value !== "MANUAL_CARD" ||
    paymentCents.value !== amount
  )
    return;
  await startPayment();
}
const paymentBalanceNumber = ref("");
const drawerId = ref<string | null>(null);
const printerMac = useReceiptPrinter();
const printerDialog = ref(false);
const receiptDraft = ref<{ print: boolean; email: boolean } | null>(null);
const receiptEditable = computed(
  () =>
    order.value?.orderStatus === OrderStatus.OPEN ||
    order.value?.orderStatus === OrderStatus.PAYING,
);
const hasBuyerEmail = computed(() =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(order.value?.personId?.email?.trim() ?? ""),
);
const receiptConfigured = computed(
  () => order.value?.receiptPrint != null && order.value?.receiptEmail != null,
);
const selectedReceiptPrinter = computed(
  () => order.value?.receiptPrinter?.printerMAC ?? printerMac.value,
);
// Default to a printed receipt without a buyer email, but only when a printer is chosen; otherwise
// anonymous sales default to no receipt instead of blocking payment until a buyer is picked.
const receiptSelection = computed(
  () =>
    receiptDraft.value ?? {
      print: receiptConfigured.value
        ? order.value!.receiptPrint!
        : receiptEditable.value &&
          !hasBuyerEmail.value &&
          !!selectedReceiptPrinter.value,
      email: receiptConfigured.value
        ? order.value!.receiptEmail!
        : receiptEditable.value && hasBuyerEmail.value,
    },
);
const receiptReady = computed(
  () =>
    !receiptEditable.value ||
    (receiptConfigured.value &&
      !receiptDraft.value &&
      (!order.value?.receiptPrint || !!order.value.receiptPrinter) &&
      (!order.value?.receiptEmail || hasBuyerEmail.value)),
);
const pendingReceiptChannels = computed(() => {
  if (
    order.value?.orderStatus !== OrderStatus.PAID &&
    order.value?.orderStatus !== OrderStatus.PROCESSED
  )
    return [];
  return [
    order.value.receiptPrint && !order.value.receiptPrintQueuedOn
      ? "print receipt not yet queued"
      : null,
    order.value.receiptEmail && !order.value.receiptEmailSentOn
      ? "email receipt not yet sent"
      : null,
  ].filter((channel): channel is string => channel != null);
});
const reference = ref("");
const memo = ref("");
const otherType = ref("");
const readerId = ref("");
const cancelDialog = ref(false);
let disposed = false;
let pollTimer: ReturnType<typeof setTimeout> | undefined;

const validId = (value: number | null | undefined) =>
  Number.isSafeInteger(value) && (value ?? 0) > 0;
const validAmount = validId;
const money = (value: number | null) =>
  value == null
    ? "Not calculated"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(value / 100);
const discountTitle = (discount: DiscountCandidate) =>
  `${discountDetails.value.find((detail) => detail.discountId === discount.discountId)?.discountName ?? `Discount #${discount.discountId}`}${discount.autoApply ? " (automatic)" : ""}`;

async function act(action: () => Promise<unknown>): Promise<boolean> {
  if (busy.value) {
    notification.addSnackbar(
      "Wait for the current shop operation to finish.",
      "error",
    );
    return false;
  }
  working.value = true;
  errorMessage.value = "";
  try {
    await action();
    await ensureReceiptDelivery();
    await Promise.all([loadLineNames(), loadDiscountNames()]);
    return true;
  } catch (error) {
    errorMessage.value = requestErrorMessage(error);
    notification.addSnackbar(errorMessage.value, "error");
    return false;
  } finally {
    working.value = false;
  }
}

async function allPages<T>(
  fetch: (page: number) => Promise<PaginationResult<T>>,
): Promise<T[]> {
  const items: T[] = [];
  let page = 1;
  let total = Infinity;
  while (items.length < total) {
    const result = await fetch(page++);
    items.push(...result.items);
    total = result.totalCount;
    if (items.length >= total) return items;
    if (!result.items.length)
      throw new Error("The catalog changed while loading; refresh the shop.");
  }
  return items;
}

async function loadCatalog() {
  loadingCatalog.value = true;
  try {
    const drawerParams = {
      ...emptySearchParams<CashDrawerStaffSearchParams>(
        CashDrawerStaffSearchParamsMeta,
      ),
      sort: [{ fieldName: "cashDrawerId", order: SortOrder.ASC }],
    };
    const [catalogLookup, cashDrawers] = await Promise.all([
      loadStaffCatalog(
        catalog,
        initialPurchaseComplete.value ? undefined : props.restockBalanceType,
      ),
      allPages((page) =>
        useCashDrawerService().searchCashDrawerStaff(drawerParams, page, 100),
      ),
    ]);
    if (disposed) return;
    instances.value = catalogLookup.items;
    itemCatalogNames.value = catalogLookup.itemCatalogNames;
    itemCategoryIds.value = catalogLookup.itemCategoryIds;
    itemAgreementIds.value = catalogLookup.itemAgreementIds;
    categoryAgreementIds.value = catalogLookup.categoryAgreementIds;
    globalAgreementIds.value = catalogLookup.globalAgreementIds;
    agreementNames.value = catalogLookup.agreementNames;
    shopCategories.value = catalogLookup.categories;
    drawers.value = cashDrawers;
  } finally {
    loadingCatalog.value = false;
  }
}

async function loadReaders() {
  const result = await useStripeService().readersStaff();
  if (!disposed) {
    readers.value = result;
    if (
      !readers.value.some(
        (reader) =>
          reader.readerId === readerId.value && reader.status === "online",
      )
    )
      readerId.value = "";
  }
}

async function loadLineNames() {
  const currentLines = lines.value;
  const currentOrder = orderRef.value;
  receiptLineDetails.value = {};
  const details = currentOrder
    ? await loadReceiptLineDetails(currentOrder, "Staff")
    : {};
  const names = await Promise.all(
    currentLines.map(async (line) => {
      const instance = await line.itemInstanceIdLazy();
      const item = await instance.itemIdLazy();
      return [
        line.assignId,
        item.itemName ?? `Item #${item.assignId}`,
      ] as const;
    }),
  );
  if (!disposed && lines.value === currentLines) {
    lineNames.value = Object.fromEntries(names);
    receiptLineDetails.value = details;
  }
}

async function loadDiscountNames() {
  const details = await Promise.all(
    [...new Set(discounts.value.map((discount) => discount.discountId))].map(
      (id) => useDiscountService().getCatalogDiscountStaff(id),
    ),
  );
  if (!disposed) discountDetails.value = details;
}

async function openOrder() {
  await act(() =>
    session.open(
      existingOrderId != null ||
        orderRef.value != null ||
        contextBuyerId.value == null
        ? null
        : { personId: contextBuyerId.value },
      existingOrderId == null ? null : { assignId: existingOrderId },
    ),
  );
}

async function newSale() {
  await act(async () => {
    session.newSale();
    existingOrderId = undefined;
    await session.open(
      contextBuyerId.value == null ? null : { personId: contextBuyerId.value },
    );
  });
}

async function add(request: AddToCart): Promise<boolean> {
  if (!(await act(() => session.add(request)))) return false;
  if (request.updateLine) return true;
  if (!initialPurchaseComplete.value && props.itemType != null) {
    initialPurchaseComplete.value = true;
    if (props.restockBalanceType != null) {
      instances.value = [];
      await act(loadCatalog);
    }
  }
  return true;
}

const catalogPurchase = ref<InstanceType<typeof StaffCatalogPurchase> | null>(
  null,
);
const recipientLineId = ref<number | null>(null);
const recipientDialog = computed({
  get: () => recipientLineId.value != null,
  set: (open: boolean) => {
    if (!open) recipientLineId.value = null;
  },
});
const instanceById = computed(
  () => new Map(instances.value.map((item) => [item.assignId, item])),
);
const staffLine = (line: ReceiptLine) =>
  lines.value.find((item) => item.assignId === line.assignId);
const lineCatalogItem = (line: ReceiptLine) =>
  instanceById.value.get(line.itemInstanceIdTargetIds.assignId);

function canConfigureLine(line: ReceiptLine) {
  const type = lineCatalogItem(line)?.itemId.itemType;
  return (
    isOpen.value &&
    type != null &&
    type !== ItemType.PAID_CLASS &&
    staffLine(line)?.classSignup == null
  );
}

/** Products and guest privileges have no recipient of their own. */
function canChangeRecipient(line: ReceiptLine) {
  const type = lineCatalogItem(line)?.itemId.itemType;
  return (
    canConfigureLine(line) &&
    type !== ItemType.PRODUCT &&
    type !== ItemType.GUEST_PRIVS
  );
}

async function configureLine(line: ReceiptLine) {
  const current = staffLine(line);
  const item = lineCatalogItem(line);
  if (!current || !item) return;
  let personId: number | null = null;
  if (
    !(await act(async () => {
      personId = (await current.personIdLazy())?.personId ?? null;
    }))
  )
    return;
  const details = receiptLineDetails.value[line.assignId];
  // The catalog's disabled prop follows `busy` on the next render.
  await nextTick();
  catalogPurchase.value?.configureLine(item, {
    lineId: line.assignId,
    recipient:
      personId == null
        ? null
        : { personId, name: current.buyerName ?? `Person #${personId}` },
    quantity: current.quantity ?? 1,
    specifiedCents: current.amountSpecifiedCents,
    cardNumber: details?.cardNumber ?? null,
    balanceNumber: details?.balanceNumber ?? null,
    donation: details?.donation ?? null,
    membershipId: details?.guestMembership?.assignId ?? null,
  });
}

async function changeLineRecipient(person: PersonStaffSearchResult) {
  if (!Number.isSafeInteger(person.personId) || person.personId <= 0) {
    notification.addSnackbar("Select a valid person.", "error");
    return;
  }
  const current = lines.value.find(
    (item) => item.assignId === recipientLineId.value,
  );
  if (!current) return;
  if (
    await add({
      itemInstance: { assignId: current.itemInstanceIdTargetIds.assignId },
      person: { personId: person.personId },
      quantity: current.quantity ?? 1,
      specifiedAmountCents: current.amountSpecifiedCents,
      parent: null,
      options: {
        cardNum: null,
        balanceNumber: null,
        membership: null,
        donation: null,
      },
      updateLine: { assignId: current.assignId },
    })
  )
    recipientLineId.value = null;
}

async function changeBuyer(person: PersonStaffSearchResult) {
  if (!Number.isSafeInteger(person.personId) || person.personId <= 0) {
    notification.addSnackbar("Select a valid person.", "error");
    return;
  }
  const changed = person.personId !== order.value?.personId?.personId;
  if (
    await act(async () => {
      await session.changeBuyer({ personId: person.personId });
      if (changed)
        receiptDraft.value = {
          print: !hasBuyerEmail.value,
          email: hasBuyerEmail.value,
        };
    })
  )
    buyerDialog.value = false;
}

async function applyCode() {
  if (
    code.value.trim() &&
    (await act(() => session.applyCode(code.value.trim())))
  )
    code.value = "";
}

/**
 * Applies the chosen discount to every eligible line that doesn't have it. Variable discounts
 * without a catalog default start at zero; staff set the value from the line's chip.
 */
async function addDiscount(discountId: number | null) {
  if (discountId == null) return;
  const startingTerms = (candidate: DiscountCandidate) => {
    if (
      candidate.discountType === DiscountType.VARIABLE ||
      (candidate.discountType === DiscountType.VARIABLE_AMOUNT &&
        candidate.amountCents == null)
    )
      return { specifiedCents: 0, specifiedPercent: null };
    if (
      candidate.discountType === DiscountType.VARIABLE_PERCENT &&
      candidate.percent == null
    )
      return { specifiedCents: null, specifiedPercent: "0" };
    return { specifiedCents: null, specifiedPercent: null };
  };
  const groups = new Map<string, DiscountCandidate[]>();
  for (const candidate of linesMissingDiscount(discountId)) {
    const key = JSON.stringify(startingTerms(candidate));
    groups.set(key, [...(groups.get(key) ?? []), candidate]);
  }
  await act(async () => {
    for (const [key, candidates] of groups)
      await session.applyDiscount({
        discount: { discountId },
        lineItems: candidates.map((candidate) => ({
          assignId: candidate.lineItem.assignId,
        })),
        ...(JSON.parse(key) as ReturnType<typeof startingTerms>),
      });
  });
}

async function changeLineDiscount(
  line: LineItemRef,
  discount: ReceiptLineDetails["discounts"][number],
  terms: DiscountTerms,
) {
  if (discount.discountId == null) return;
  await act(() =>
    session.applyDiscount({
      discount: { discountId: discount.discountId! },
      lineItems: [{ assignId: line.assignId }],
      ...terms,
    }),
  );
}

async function startPayment() {
  if (!receiptReady.value) return;
  if (
    !validAmount(paymentCents.value) ||
    !validAmount(paymentAmountCents.value) ||
    availableCents.value == null ||
    paymentAmountCents.value! >
      availableCents.value +
        (paymentType.value === "TERMINAL" || paymentType.value === "MANUAL_CARD"
          ? (activeCardAttempt.value?.amountCents ?? 0)
          : 0) ||
    (paymentType.value === PaymentType.CASH && !drawerId.value)
  ) {
    notification.addSnackbar(
      "Enter a valid payment amount and select a cash drawer for cash payments.",
      "error",
    );
    return;
  }
  const selectedType = paymentType.value;
  const existing = activeCardAttempt.value;
  const tenderedChangeCents =
    selectedType === PaymentType.CASH ? cashChangeCents.value : 0;
  const started = await act(async () => {
    if (selectedType === "TERMINAL" && existing) {
      if (existing.amountCents !== paymentCents.value)
        throw new Error(
          "Cancel the pending card attempt before changing its terminal amount.",
        );
      if (!existing.clientSecret)
        await session.preparePayment(existing.payment);
      await session.sendToReader(existing.payment, readerId.value.trim());
      return;
    }
    let balance = null;
    if (selectedType === PaymentType.BALANCE) {
      const number = paymentBalanceNumber.value.trim();
      if (!number) throw new Error("Enter a balance account number.");
      const found = await useBalanceService().findBalanceByNumStaff(number);
      balance = { assignId: found.assignId };
    }
    const attempt = await session.startPayment({
      type:
        selectedType === "TERMINAL" || selectedType === "MANUAL_CARD"
          ? PaymentType.STRIPE
          : selectedType,
      amountCents: paymentAmountCents.value!,
      balance,
      cashDrawer:
        paymentType.value === PaymentType.CASH && drawerId.value
          ? { cashDrawerId: drawerId.value }
          : null,
      tenderedCents:
        paymentType.value === PaymentType.CASH ? paymentCents.value! : null,
      reference: reference.value.trim() || null,
      memo: memo.value.trim() || null,
      otherType:
        paymentType.value === PaymentType.OTHER
          ? otherType.value.trim() || null
          : null,
    });
    if (selectedType === "TERMINAL")
      await session.sendToReader(attempt.payment, readerId.value.trim());
  });
  if (started && selectedType === PaymentType.CASH && order.value)
    lastCashChange.value = {
      orderId: order.value.assignId,
      cents: tenderedChangeCents,
    };
  if (started && selectedType !== "MANUAL_CARD" && selectedType !== "TERMINAL")
    paymentCents.value =
      paymentType.value === PaymentType.CASH || availableCents.value == null
        ? undefined
        : Math.max(0, availableCents.value);
}

async function checkout() {
  if (!receiptReady.value) return;
  checkingAgreements.value = true;
  try {
    await notifyMissingAgreements();
  } finally {
    checkingAgreements.value = false;
  }
  if (!disposed) await act(session.checkout);
}

async function notifyMissingAgreements() {
  const personId = order.value?.personId?.personId;
  if (personId == null) return;

  try {
    const requiredIds = new Set(globalAgreementIds.value);
    const itemIds: CatalogItemRef[] = [];
    for (const line of lines.value) {
      const instance = await line.itemInstanceIdLazy();
      const itemId = instance.itemIdTargetIds.assignId;
      itemIds.push({ assignId: itemId });
      for (const categoryId of itemCategoryIds.value.get(itemId) ?? []) {
        for (const agreementId of categoryAgreementIds.value.get(categoryId) ??
          [])
          requiredIds.add(agreementId);
      }
      for (const agreementId of itemAgreementIds.value.get(itemId) ?? [])
        requiredIds.add(agreementId);
    }
    if (requiredIds.size === 0) return;

    const agreedIds = new Set<number>();
    let page = 1;
    let totalPages = 1;
    do {
      const result = await usePersonService().searchPersonAgreementsStaff({
        personIdExact: personId,
        query: null,
        page,
        perPage: 100,
        itemIds,
        agreements: [...requiredIds].map((assignId) => ({ assignId })),
        sort: [],
      });
      result.items
        .filter((row) => row.isAgreed)
        .forEach((row) => agreedIds.add(row.agreement.assignId));
      totalPages = Math.ceil(result.totalCount / 100);
      page++;
    } while (page <= totalPages);
    const missingNames = [...requiredIds]
      .filter((id) => !agreedIds.has(id))
      .map((id) => agreementNames.value.get(id) ?? `Agreement #${id}`);

    if (missingNames.length > 0) {
      notification.addSnackbar(
        `Missing required agreements: ${missingNames.join(", ")}. Staff may continue with payment.`,
        "error",
        10000,
      );
    }
  } catch {
    notification.addSnackbar(
      "Could not check required agreements. Staff checkout can continue.",
      "warning",
    );
  }
}

async function reconcile() {
  if (!receiptReady.value) return;
  await act(session.reconcile);
}

const simulationOutcome = ref("");

async function simulateReader(simulation: StripeReaderSimulation) {
  simulationOutcome.value = "";
  await act(async () => {
    const result = await useStripeService().simulateReaderStaff({
      readerId: readerId.value.trim(),
      simulation,
      tipCents: null,
    });
    simulationOutcome.value = `Reader ${result.actionType ?? "action"}: ${result.actionStatus ?? "unknown"}${result.failureMessage ? ` (${result.failureMessage})` : ""}`;
    if (receiptReady.value) await session.reconcile();
  });
}

async function ensureReceiptDelivery() {
  if (!receiptEditable.value || receiptReady.value) return;
  const selection = receiptSelection.value;
  const printer = selectedReceiptPrinter.value;
  if (selection.print && !printer) return;
  await session.configureReceiptDelivery(
    selection.print,
    selection.email,
    selection.print && printer ? { printerMAC: printer } : null,
  );
  receiptDraft.value = null;
}

async function toggleReceipt(kind: "print" | "email") {
  if (busy.value || !receiptEditable.value) return;
  receiptDraft.value = {
    ...receiptSelection.value,
    [kind]: !receiptSelection.value[kind],
  };
  if (receiptDraft.value.print && !selectedReceiptPrinter.value)
    printerDialog.value = true;
  await act(ensureReceiptDelivery);
}

async function cancelOrder() {
  const wasComplete = isComplete.value;
  if (await act(session.cancel)) {
    cancelDialog.value = false;
    existingOrderId = undefined;
    if (wasComplete) emit("complete");
    emit("close");
  }
}

async function cancelCompletedPayment() {
  const payment = paymentToCancel.value;
  if (!payment) return;
  if (await act(() => session.cancelPayment(payment)))
    paymentToCancel.value = null;
}

function schedulePoll() {
  if (pollTimer) clearTimeout(pollTimer);
  if (
    disposed ||
    !receiptReady.value ||
    (order.value?.orderStatus !== OrderStatus.PAYING &&
      !(
        order.value?.orderStatus === OrderStatus.PROCESSED &&
        hasPendingPayment.value
      ))
  )
    return;
  pollTimer = setTimeout(async () => {
    if (disposed) return;
    try {
      if (!busy.value && receiptReady.value) {
        await session.reconcileBackground();
        await loadLineNames();
        if (!disposed) pollError.value = "";
      }
    } catch (error) {
      if (!disposed) pollError.value = requestErrorMessage(error);
    } finally {
      schedulePoll();
    }
  }, 5000);
}

watch([paymentType, paymentCents, receiptReady, showPaymentInput], () => {
  clearTimeout(manualSetupTimer);
  suppressManualSetup = false;
  manualSetupRequested = true;
  if (paymentType.value === "MANUAL_CARD" && showPaymentInput.value)
    manualSetupTimer = setTimeout(() => {
      if (!suppressManualSetup) void prepareManualCard();
    }, 400);
});
watch(busy, (value) => {
  if (
    !value &&
    manualSetupRequested &&
    !suppressManualSetup &&
    paymentType.value === "MANUAL_CARD" &&
    showPaymentInput.value
  ) {
    clearTimeout(manualSetupTimer);
    manualSetupTimer = setTimeout(() => void prepareManualCard(), 400);
  }
});
watch(
  () => order.value?.orderSource,
  (source) => {
    if (source === "ONLINE" && paymentType.value === "TERMINAL")
      paymentType.value = "MANUAL_CARD";
  },
);
watch(orderRef, (value) => emit("order-selected", value?.assignId ?? null));
watch(
  () => `${order.value?.assignId}:${order.value?.personId?.personId}`,
  () => {
    receiptDraft.value = null;
  },
  { flush: "sync" },
);
watch(printerMac, async (value) => {
  if (!receiptEditable.value || !receiptSelection.value.print || !value) return;
  receiptDraft.value = { ...receiptSelection.value };
  await act(() =>
    session
      .configureReceiptDelivery(
        receiptSelection.value.print,
        receiptSelection.value.email,
        { printerMAC: value },
      )
      .then(() => {
        receiptDraft.value = null;
      }),
  );
});
watch(isComplete, (value) => {
  if (value) emit("complete");
});
watch(
  paymentType,
  (type) => {
    paymentCents.value =
      type === PaymentType.CASH || availableCents.value == null
        ? undefined
        : (type === "MANUAL_CARD" || type === "TERMINAL") &&
            activeCardAttempt.value
          ? activeCardAttempt.value.amountCents
          : Math.max(0, availableCents.value);
  },
  { flush: "sync" },
);
watch(availableCents, (value, previous) => {
  if (
    (paymentType.value === "MANUAL_CARD" || paymentType.value === "TERMINAL") &&
    activeCardAttempt.value
  ) {
    if (paymentCents.value == null || paymentCents.value === previous)
      paymentCents.value = activeCardAttempt.value.amountCents;
    return;
  }
  if (paymentType.value === PaymentType.CASH) {
    if (value !== previous) paymentCents.value = undefined;
    return;
  }
  if (
    paymentCents.value == null ||
    paymentCents.value === Math.max(0, previous ?? 0)
  )
    paymentCents.value = value == null ? undefined : Math.max(0, value);
});
watch(
  [() => order.value?.orderStatus, hasPendingPayment, receiptReady],
  ([status, pending]) => {
    if (status !== OrderStatus.PAYING && !pending) pollError.value = "";
    schedulePoll();
  },
);
onMounted(async () => {
  if (!(await act(loadCatalog))) return;
  await openOrder();
  await act(loadReaders);
});
onBeforeUnmount(() => {
  clearTimeout(manualSetupTimer);
  disposed = true;
  session.dispose();
  if (pollTimer) clearTimeout(pollTimer);
});
</script>

<style scoped>
.staff-order-shop {
  height: calc(100dvh - 48px);
  min-height: calc(100dvh - 48px);
  max-height: calc(100dvh - 48px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.shop-header,
.shop-actions,
.staff-order-shop > .v-alert,
.staff-order-shop > .v-progress-linear {
  flex: 0 0 auto;
}

.shop-actions {
  align-self: stretch;
  box-sizing: border-box;
  flex-wrap: wrap;
  justify-content: flex-start;
  min-width: 0;
  width: 100%;
  max-height: 35dvh;
  overflow-y: auto;
}

.shop-cancel-actions,
.shop-proceed-actions {
  min-width: 0;
  max-width: 100%;
}

.staff-order-shop > .v-alert {
  max-height: 20dvh;
  overflow-y: auto;
}

.shop-body {
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
  margin-top: 0;
  margin-bottom: 0;
}

.shop-tools {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.shop-tools :deep(.catalog-purchase) {
  height: 100%;
}

.shop-edit-card {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}
.shop-completion-notice {
  flex: 0 0 auto;
}

.shop-summary-card {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.cart-panel {
  display: flex;
  flex-direction: column;
}

.payment-panel,
.completed-payment-panel,
.cart-panel {
  min-height: 0;
  height: 100%;
  overflow-y: auto;
  scrollbar-gutter: stable;
}

.payment-type-tab {
  min-width: 110px;
}

@media (max-width: 959px) {
  .shop-tools {
    height: 60%;
  }
  .cart-panel {
    height: 40%;
  }
}
</style>
